import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api';

interface Secret {
  id: string;
  name: string;
  secret_type?: string;
}

interface ModelParameter {
  name: string;
  label: string;
  type: 'number' | 'string' | 'boolean';
  default: any;
  min?: number;
  max?: number;
  step?: number;
  description?: string;
}

interface LLMModel {
  name: string;
  value: string;
  cost_per_1k_input: number;
  cost_per_1k_output: number;
  model_parameters?: ModelParameter[];
}

interface LLMEndpoint {
  name: string;
  path: string;
  description: string;
  is_default?: boolean;
}

interface LLMProvider {
  name: string;
  enum_value: string;
  endpoints: LLMEndpoint[];
  icon: string;
  api_key_label: string;
  models: LLMModel[];
}

interface GatewayConfig {
  llm?: {
    paths?: Array<{ id: string; name: string; prefix: string }>;
    providers?: LLMProvider[];
  };
  mcp_proxies?: {
    paths?: Array<{ id: string; name: string; prefix: string }>;
  };
}

interface BasicInfoStepProps {
  provider: string;
  initialName?: string;
  initialDescription?: string;
  initialModel?: string;
  initialEndpoint?: string;
  initialSecretId?: string;
  initialModelParameters?: Record<string, any>;
  initialAzureApiVersion?: string;
  initialRegion?: string;
  onChange: (data: {
    name: string;
    description: string;
    model: string;
    endpoint: string;
    secretId: string;
    modelParameters?: Record<string, any>;
    azureApiVersion?: string;
    region?: string;
  }) => void;
  onNext: () => void;
  onBack: () => void;
  onCancel: () => void;
}

const BasicInfoStep: React.FC<BasicInfoStepProps> = ({
  provider,
  initialName = '',
  initialDescription = '',
  initialModel = '',
  initialEndpoint = '',
  initialSecretId = '',
  initialModelParameters = {},
  initialAzureApiVersion = '',
  initialRegion = '',
  onChange,
  onNext,
  onBack,
  onCancel
}) => {
  const navigate = useNavigate();
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [model, setModel] = useState(initialModel);
  const [endpoint, setEndpoint] = useState(initialEndpoint);
  const [secretId, setSecretId] = useState(initialSecretId);
  const [modelParameters, setModelParameters] = useState<Record<string, any>>(initialModelParameters);
  const [azureApiVersion, setAzureApiVersion] = useState(initialAzureApiVersion);
  const [region, setRegion] = useState(initialRegion);
  const [error, setError] = useState('');
  const [providerConfig, setProviderConfig] = useState<LLMProvider | null>(null);
  const [secrets, setSecrets] = useState<Secret[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProviderConfig();
    fetchSecrets();
  }, [provider]);

  // Set default model parameters when model changes
  useEffect(() => {
    if (!providerConfig || !model) return;
    
    const selectedModel = providerConfig.models.find(m => m.value === model);
    if (selectedModel?.model_parameters) {
      const defaults: Record<string, any> = {};
      selectedModel.model_parameters.forEach(param => {
        defaults[param.name] = param.default;
      });
      setModelParameters(defaults);
    }
  }, [model, providerConfig]);

  const loadProviderConfig = async () => {
    try {
      const response = await apiClient.get('/pipes/config');
      const config: GatewayConfig = response.data;
      if (config.llm?.providers) {
        const selectedProvider = config.llm.providers.find(p => p.enum_value === provider);
        setProviderConfig(selectedProvider || null);
        if (selectedProvider && !initialModel && selectedProvider.models.length > 0) {
          setModel(selectedProvider.models[0].value);
        }
        if (selectedProvider && !initialEndpoint) {
          // Set the default endpoint (or first one if no default is marked)
          const defaultEndpoint = selectedProvider.endpoints.find(e => e.is_default) || selectedProvider.endpoints[0];
          if (defaultEndpoint) {
            setEndpoint(defaultEndpoint.path);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load provider config:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSecrets = async () => {
    try {
      const response = await apiClient.get('/secrets/');
      const allSecrets = response.data || [];
      const apiKeySecrets = allSecrets.filter((s: any) => s.secret_type === 'ApiKey');
      setSecrets(apiKeySecrets);
    } catch (err: any) {
      console.error('Failed to fetch secrets:', err);
    }
  };

  const handleNavigateToCreateSecret = () => {
    navigate('/secrets/new', {
      state: {
        returnTo: '/pipes/new',
        defaultType: 'ApiKey'
      }
    });
  };

  const getProviderName = () => {
    return providerConfig?.name || provider;
  };

  const getDefaultModels = () => {
    return providerConfig?.models || [];
  };

  const getEndpoints = () => {
    return providerConfig?.endpoints || [];
  };

  const getDefaultEndpoint = () => {
    if (!providerConfig?.endpoints) return '';
    const defaultEndpoint = providerConfig.endpoints.find(e => e.is_default) || providerConfig.endpoints[0];
    return defaultEndpoint?.path || '';
  };

  const getSecretLabel = () => {
    const labels: Record<string, string> = {
      'openai': 'OpenAI API Key',
      'anthropic': 'Anthropic API Key',
      'bedrock': 'AWS Access Credentials',
      'azure': 'Azure API Key',
      'gemini': 'Google API Key'
    };
    return labels[provider] || 'API Key';
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Please enter a name for this pipe');
      return;
    }

    if (!model.trim()) {
      setError('Please select or enter a model');
      return;
    }

    if (!secretId) {
      setError('Please select or create an API key secret');
      return;
    }

    if (!endpoint.trim()) {
      setError('Please enter the endpoint URL');
      return;
    }

    onChange({
      name: name.trim(),
      description: description.trim(),
      model: model.trim(),
      endpoint: endpoint.trim(),
      secretId: secretId,
      azureApiVersion: provider === 'azure' ? azureApiVersion.trim() : undefined,
      region: provider === 'bedrock' ? region.trim() : undefined,
      modelParameters: modelParameters
    });
    onNext();
  };

  if (loading) {
    return (
      <div className="card shadow">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-3">Loading provider configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card shadow">
      <div className="card-body">
        <h4 className="mb-3">
          <i className="fas fa-info-circle mr-2"></i>
          Basic Information - {getProviderName()}
        </h4>

        <p className="text-muted mb-4">
          Provide basic information about your LLM Pipe configuration.
        </p>

        {error && (
          <div className="alert alert-danger">{error}</div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label htmlFor="name" className="form-label">
              <strong>Pipe Name *</strong>
            </label>
            <input
              type="text"
              className="form-control"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={`My ${getProviderName()} Pipe`}
              required
            />
            <small className="form-text text-muted">
              A friendly name to identify this LLM pipe
            </small>
          </div>

          <div className="mb-3">
            <label htmlFor="description" className="form-label">
              <strong>Description</strong>
            </label>
            <input
              className="form-control"
              id="description"              
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description of the purpose of this pipe"
            />
          </div>

          <div className="mb-3">
            <label htmlFor="model" className="form-label">
              <strong>Model *</strong>
            </label>
            <select
              className="form-control dropdown-styling"
              id="model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              required
            >
              <option value="">Select a model...</option>
              {getDefaultModels().map(m => (
                <option key={m.value} value={m.value}>{m.name}</option>
              ))}
              <option value="custom">Custom model...</option>
            </select>
            {model === 'custom' && (
              <input
                type="text"
                className="form-control mt-2"
                placeholder="Enter custom model name"
                onChange={(e) => setModel(e.target.value)}
              />
            )}
            <small className="form-text text-muted">
              The LLM model to use for this pipe
            </small>
          </div>

          <div className="mb-4">
            <label htmlFor="endpoint" className="form-label">
              <strong>Endpoint *</strong>
            </label>
            <select
              className="form-control dropdown-styling"
              id="endpoint"
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              required
            >
              <option value="">Select an endpoint...</option>
              {getEndpoints().map((ep, idx) => (
                <option key={idx} value={ep.path}>
                  {ep.name} {ep.is_default && '(Default)'}
                </option>
              ))}
            </select>
            <small className="form-text text-muted">
              {(() => {
                const selectedEp = getEndpoints().find(e => e.path === endpoint);
                return selectedEp?.description || `API endpoint for ${getProviderName()}`;
              })()}
            </small>
          </div>

          {provider === 'azure' && (
            <div className="mb-4">
              <label htmlFor="azureApiVersion" className="form-label">
                <strong>API Version</strong>
              </label>
              <input
                type="text"
                className="form-control"
                id="azureApiVersion"
                value={azureApiVersion}
                onChange={(e) => setAzureApiVersion(e.target.value)}
                placeholder="2024-02-15-preview"
              />
              <small className="form-text text-muted">
                Azure OpenAI API version (e.g., 2024-02-15-preview)
              </small>
            </div>
          )}

          {provider === 'bedrock' && (
            <div className="mb-4">
              <label htmlFor="region" className="form-label">
                <strong>AWS Region</strong>
              </label>
              <select
                className="form-control"
                id="region"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
              >
                <option value="">Select region...</option>
                <option value="us-east-1">US East (N. Virginia)</option>
                <option value="us-west-2">US West (Oregon)</option>
                <option value="eu-west-1">EU (Ireland)</option>
                <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
              </select>
              <small className="form-text text-muted">
                AWS region for Bedrock access
              </small>
            </div>
          )}

          {/* API Key / Secret Selection */}
          <div className="mb-4">
            <label htmlFor="secretId" className="form-label">
              <strong>{getSecretLabel()} *</strong>
            </label>
            <div className="input-group">
              <select
                className="form-control dropdown-styling"
                id="secretId"
                value={secretId}
                onChange={(e) => setSecretId(e.target.value)}
                disabled={loading}
                required
              >
                <option value="">Select a secret...</option>
                {secrets.map(secret => (
                  <option key={secret.id} value={secret.id}>
                    {secret.name}
                  </option>
                ))}
              </select>
            </div>
            <small className="form-text text-muted">
              Select the API key secret to use for authentication
            </small>
          </div>

          <div className="d-flex justify-content-between mt-4">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onBack}
            >
              <i className="fas fa-arrow-left mr-1"></i>
              Back
            </button>
            <button type="submit" className="btn btn-primary">
              Next
              <i className="fas fa-arrow-right ml-1"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BasicInfoStep;
